/**
 * Retail PDP — isolated variant resolution + gallery sync.
 * Only runs inside [data-retail-product]. Does not touch custom/default PDP scripts.
 */
(function () {
  'use strict';

  var DEBUG =
    (typeof Shopify !== 'undefined' && Shopify.designMode === true) ||
    (typeof window !== 'undefined' &&
      (window.location.search.indexOf('retail_debug=1') !== -1 ||
        window.localStorage.getItem('retail_debug') === '1'));

  function log() {
    if (!DEBUG || !window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[retail-product]');
    console.log.apply(console, args);
  }

  function warnDev() {
    if (!window.console || !console.warn) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[retail-product]');
    console.warn.apply(console, args);
  }

  function normalizeOptionValue(value) {
    if (value == null) return '';
    return String(value)
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function optionValuesEqual(a, b) {
    return normalizeOptionValue(a).toLowerCase() === normalizeOptionValue(b).toLowerCase();
  }

  function mediaIdOf(el) {
    if (!el) return '';
    return String(el.getAttribute('data-media-id') || '');
  }

  function variantIdsOf(el) {
    if (!el) return '';
    return String(el.getAttribute('data-variant-ids') || '');
  }

  function mediaSelector(mediaId) {
    var id = String(mediaId).replace(/"/g, '');
    return '[data-media-id="' + id + '"]';
  }

  function formatMoney(cents, moneyFormat) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, moneyFormat);
    }
    var amount = (Number(cents) / 100).toFixed(2);
    return moneyFormat ? moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount) : amount;
  }

  function getUrlVariantId() {
    try {
      var params = new URLSearchParams(window.location.search);
      var raw = params.get('variant');
      if (!raw) return null;
      return String(raw);
    } catch (e) {
      return null;
    }
  }

  function replaceVariantInUrl(variantId) {
    try {
      var url = new URL(window.location.href);
      if (variantId) {
        url.searchParams.set('variant', String(variantId));
      } else {
        url.searchParams.delete('variant');
      }
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    } catch (e) {
      /* ignore */
    }
  }

  function findVariantByOptions(variants, selectedOptions) {
    if (!variants || !selectedOptions) return null;
    for (var i = 0; i < variants.length; i++) {
      var variant = variants[i];
      var opts = variant.options || [];
      if (opts.length !== selectedOptions.length) continue;
      var match = true;
      for (var j = 0; j < opts.length; j++) {
        if (!optionValuesEqual(opts[j], selectedOptions[j])) {
          match = false;
          break;
        }
      }
      if (match) return variant;
    }
    return null;
  }

  function findVariantById(variants, id) {
    if (id == null || id === '') return null;
    var needle = String(id);
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === needle) return variants[i];
    }
    return null;
  }

  function firstAvailableOrFirst(variants) {
    if (!variants || !variants.length) return null;
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available) return variants[i];
    }
    return variants[0];
  }

  function RetailProduct(root) {
    this.root = root;
    this.productId = Number(root.getAttribute('data-product-id'));
    this.sectionId = root.getAttribute('data-section-id') || '';
    this.moneyFormat = root.getAttribute('data-money-format') || '{{amount}}';
    this.form =
      root.querySelector('#retail-product-form') ||
      root.querySelector('form.retail-product__form') ||
      (root.querySelector('[data-retail-product-form-marker]') &&
        root.querySelector('[data-retail-product-form-marker]').closest('form'));
    this.stickyForm = root.querySelector('[data-retail-sticky-form]');
    this.idInput = this.form && this.form.querySelector('[data-retail-variant-id]');
    this.stickyIdInput = this.stickyForm && this.stickyForm.querySelector('[data-retail-variant-id]');
    if (this.form) {
      this.form.setAttribute('data-retail-product-form', '');
    }
    this.submitBtns = root.querySelectorAll('[data-retail-add-to-cart]');
    this.statusEl = root.querySelector('[data-retail-variant-status]');
    this.priceEls = root.querySelectorAll('[data-retail-price]');
    this.compareEls = root.querySelectorAll('[data-retail-compare-price]');
    this.skuEls = root.querySelectorAll('[data-retail-sku]');
    this.stickyImg = root.querySelector('[data-retail-sticky-image]');
    this.optionInputs = root.querySelectorAll('[data-retail-option-input]');
    this.variants = [];
    this.optionNames = [];
    this.bound = false;
    this.submitting = false;

    var jsonEl = root.querySelector('[data-retail-variants-json]');
    if (jsonEl) {
      try {
        this.variants = JSON.parse(jsonEl.textContent || '[]');
      } catch (e) {
        warnDev('Failed to parse variants JSON', e);
        this.variants = [];
      }
    }

    var namesEl = root.querySelector('[data-retail-option-names-json]');
    if (namesEl) {
      try {
        this.optionNames = JSON.parse(namesEl.textContent || '[]');
      } catch (e) {
        this.optionNames = [];
      }
    }
  }

  RetailProduct.prototype.getSelectedOptions = function () {
    var count = this.optionNames.length;
    var selected = [];
    for (var i = 0; i < count; i++) {
      var name = 'options[' + i + ']';
      var checked = this.root.querySelector(
        '[data-retail-option-input][data-option-index="' + i + '"]:checked'
      );
      var select = this.root.querySelector(
        'select[data-retail-option-input][data-option-index="' + i + '"]'
      );
      var input = checked || select;
      if (!input) {
        selected.push(null);
      } else {
        selected.push(normalizeOptionValue(input.value));
      }
    }
    return selected;
  };

  RetailProduct.prototype.setOptionValuesFromVariant = function (variant) {
    if (!variant || !variant.options) return;
    var self = this;
    variant.options.forEach(function (value, index) {
      var radios = self.root.querySelectorAll(
        'input[data-retail-option-input][data-option-index="' + index + '"]'
      );
      if (radios.length) {
        Array.prototype.forEach.call(radios, function (radio) {
          var match = optionValuesEqual(radio.value, value);
          radio.checked = match;
          var label = radio.closest('[data-retail-option-label]');
          if (label) {
            label.classList.toggle('is-selected', match);
            label.setAttribute('aria-checked', match ? 'true' : 'false');
            if (label.hasAttribute('data-retail-dropdown-option')) {
              label.setAttribute('aria-selected', match ? 'true' : 'false');
            }
          }
        });
        return;
      }
      var select = self.root.querySelector(
        'select[data-retail-option-input][data-option-index="' + index + '"]'
      );
      if (select) {
        Array.prototype.forEach.call(select.options, function (opt) {
          if (optionValuesEqual(opt.value, value)) {
            select.value = opt.value;
          }
        });
      }
    });
    this.syncOptionUiLabels();
  };

  RetailProduct.prototype.syncOptionUiLabels = function () {
    var self = this;

    Array.prototype.forEach.call(this.root.querySelectorAll('[data-retail-selected-value]'), function (el) {
      var group = el.closest('[data-option-index]');
      var index = group ? group.getAttribute('data-option-index') : null;
      if (index == null) return;
      var checked = self.root.querySelector(
        'input[data-retail-option-input][data-option-index="' + index + '"]:checked'
      );
      if (checked) el.textContent = checked.value;
    });

    Array.prototype.forEach.call(this.root.querySelectorAll('[data-retail-colour]'), function (wrap) {
      var checked = wrap.querySelector('input[data-retail-option-input]:checked');
      var labelEl = wrap.querySelector('[data-retail-colour-label]');
      if (labelEl && checked) labelEl.textContent = checked.value;
    });

    Array.prototype.forEach.call(this.root.querySelectorAll('[data-retail-dropdown]'), function (wrap) {
      var checked = wrap.querySelector('input[data-retail-option-input]:checked');
      var labelEl = wrap.querySelector('[data-retail-dropdown-label]');
      if (labelEl && checked) labelEl.textContent = checked.value;
      Array.prototype.forEach.call(wrap.querySelectorAll('[data-retail-dropdown-option]'), function (opt) {
        var input = opt.querySelector('input[data-retail-option-input]');
        var on = !!(input && input.checked);
        opt.classList.toggle('is-selected', on);
        opt.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    });
  };

  RetailProduct.prototype.updateOptionAvailability = function () {
    var self = this;
    var current = this.getSelectedOptions();
    var variants = this.variants || [];

    Array.prototype.forEach.call(this.root.querySelectorAll('input[data-retail-option-input]'), function (input) {
      var index = parseInt(input.getAttribute('data-option-index'), 10);
      if (isNaN(index)) return;

      var trial = current.slice();
      trial[index] = normalizeOptionValue(input.value);

      var available = false;
      for (var i = 0; i < variants.length; i++) {
        var variant = variants[i];
        var opts = variant.options || [];
        if (opts.length !== trial.length) continue;
        var match = true;
        for (var j = 0; j < trial.length; j++) {
          if (trial[j] == null) continue;
          if (!optionValuesEqual(opts[j], trial[j])) {
            match = false;
            break;
          }
        }
        if (match && variant.available) {
          available = true;
          break;
        }
      }

      var label = input.closest('[data-retail-option-label]');
      if (label) label.classList.toggle('is-unavailable', !available);

      input.setAttribute('data-option-available', available ? 'true' : 'false');

      var ariaBase = (input.getAttribute('aria-label') || input.value || '').replace(
        /,\s*Unavailable\s*$/i,
        ''
      );
      if (!ariaBase) ariaBase = input.value || '';
      input.setAttribute('aria-label', available ? ariaBase : ariaBase + ', Unavailable');
    });
  };

  RetailProduct.prototype.updateSelectedStyles = function () {
    var labels = this.root.querySelectorAll('[data-retail-option-label]');
    Array.prototype.forEach.call(labels, function (label) {
      var input = label.querySelector('input[data-retail-option-input]');
      var selected = !!(input && input.checked);
      label.classList.toggle('is-selected', selected);
      label.setAttribute('aria-checked', selected ? 'true' : 'false');
      if (label.hasAttribute('data-retail-dropdown-option')) {
        label.setAttribute('aria-selected', selected ? 'true' : 'false');
      }
    });
    this.syncOptionUiLabels();
  };

  RetailProduct.prototype.closeAllDropdowns = function (except) {
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-retail-dropdown]'), function (wrap) {
      if (except && wrap === except) return;
      wrap.classList.remove('is-open');
      var trigger = wrap.querySelector('[data-retail-dropdown-trigger]');
      var list = wrap.querySelector('[data-retail-dropdown-list]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (list) list.setAttribute('hidden', '');
    });
  };

  RetailProduct.prototype.toggleDropdown = function (wrap, forceOpen) {
    if (!wrap) return;
    var open = typeof forceOpen === 'boolean' ? forceOpen : !wrap.classList.contains('is-open');
    if (open) this.closeAllDropdowns(wrap);
    wrap.classList.toggle('is-open', open);
    var trigger = wrap.querySelector('[data-retail-dropdown-trigger]');
    var list = wrap.querySelector('[data-retail-dropdown-list]');
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (list) {
      if (open) list.removeAttribute('hidden');
      else list.setAttribute('hidden', '');
    }
  };

  RetailProduct.prototype.setIdInputs = function (variantId) {
    var value = variantId ? String(variantId) : '';
    if (this.idInput) this.idInput.value = value;
    if (this.stickyIdInput) this.stickyIdInput.value = value;
  };

  RetailProduct.prototype.setSubmitEnabled = function (enabled, label) {
    Array.prototype.forEach.call(this.submitBtns, function (btn) {
      btn.disabled = !enabled;
      btn.setAttribute('aria-disabled', enabled ? 'false' : 'true');
      if (label) {
        var textEl = btn.querySelector('[data-retail-add-label]');
        if (textEl) textEl.textContent = label;
        else btn.textContent = label;
      }
    });
  };

  RetailProduct.prototype.setStatus = function (message, tone) {
    if (!this.statusEl) return;
    if (!message) {
      this.statusEl.hidden = true;
      this.statusEl.textContent = '';
      this.statusEl.removeAttribute('data-tone');
      return;
    }
    this.statusEl.hidden = false;
    this.statusEl.textContent = message;
    this.statusEl.setAttribute('data-tone', tone || 'info');
  };

  RetailProduct.prototype.updatePrice = function (variant) {
    if (!variant) return;
    var priceHtml = formatMoney(variant.price, this.moneyFormat);
    var compare =
      variant.compare_at_price && variant.compare_at_price > variant.price
        ? formatMoney(variant.compare_at_price, this.moneyFormat)
        : null;

    Array.prototype.forEach.call(this.priceEls, function (el) {
      el.innerHTML = priceHtml;
    });
    Array.prototype.forEach.call(this.compareEls, function (el) {
      if (compare) {
        el.hidden = false;
        el.style.display = '';
        el.innerHTML = compare;
      } else {
        el.hidden = true;
        el.style.display = 'none';
        el.innerHTML = '';
      }
    });
  };

  RetailProduct.prototype.updateSku = function (variant) {
    Array.prototype.forEach.call(this.skuEls, function (el) {
      var sku = variant && variant.sku ? variant.sku : '';
      el.textContent = sku;
      var wrap = el.closest('[data-retail-sku-wrap]');
      if (wrap) wrap.hidden = !sku;
    });
  };

  function shippingCentsFromWeightGrams(grams) {
    var weight = Number(grams) || 0;
    if (weight > 10000) return 3495;
    if (weight > 5000) return 2495;
    if (weight > 2000) return 1495;
    return 695;
  }

  RetailProduct.prototype.updateShippingCost = function (variant) {
    var el = this.root.querySelector('[data-retail-shipping-cost]');
    if (!el) return;
    if (variant && variant.free_shipping) {
      el.textContent = 'Free';
      return;
    }
    var cents = shippingCentsFromWeightGrams(variant && variant.weight);
    el.textContent = formatMoney(cents, this.moneyFormat);
  };

  RetailProduct.prototype.getFeaturedMediaId = function (variant) {
    if (!variant) return null;
    if (variant.featured_media && variant.featured_media.id) return variant.featured_media.id;
    if (variant.featured_image && variant.featured_image.id) return variant.featured_image.id;
    return null;
  };

  RetailProduct.prototype.getTilesGrid = function () {
    return (
      this.root.querySelector('.gallery-control--tiles .tcn-pdp-tiles-grid.tcn-pdp-gallery-desktop-only') ||
      this.root.querySelector('.gallery-control--tiles .tcn-pdp-tiles-grid') ||
      this.root.querySelector('.gallery-control[data-pdp-desktop-layout="tiles"] .tcn-pdp-tiles-grid')
    );
  };

  RetailProduct.prototype.isTilesGalleryActive = function () {
    var grid = this.getTilesGrid();
    if (!grid) return false;
    var style = window.getComputedStyle(grid);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    return grid.offsetParent !== null || grid.getClientRects().length > 0;
  };

  RetailProduct.prototype.getSelectedOptionSummary = function () {
    var parts = [];
    Array.prototype.forEach.call(
      this.root.querySelectorAll('[data-retail-option-input]:checked'),
      function (input) {
        var name = input.getAttribute('data-option-name') || '';
        var value = input.value || '';
        if (name && value) parts.push(name + ': ' + value);
        else if (value) parts.push(value);
      }
    );
    return parts.join(' · ');
  };

  RetailProduct.prototype.syncAiSearchImages = function (imageUrl, variant) {
    if (!imageUrl) return;
    var absolute = imageUrl;
    if (absolute.indexOf('//') === 0) absolute = window.location.protocol + absolute;
    try {
      var og = document.querySelector('meta[property="og:image"]');
      if (og) og.setAttribute('content', absolute);
      var tw = document.querySelector('meta[name="twitter:image"]');
      if (tw) tw.setAttribute('content', absolute);
    } catch (eMeta) {
      /* ignore */
    }

    try {
      var scripts = document.querySelectorAll('script[type="application/ld+json"]');
      Array.prototype.forEach.call(scripts, function (script) {
        var raw = script.textContent;
        if (!raw || raw.indexOf('"@type"') === -1 || raw.indexOf('Product') === -1) return;
        var data = JSON.parse(raw);
        var nodes = Array.isArray(data) ? data : data['@graph'] ? data['@graph'] : [data];
        var changed = false;
        nodes.forEach(function (node) {
          if (!node || node['@type'] !== 'Product') return;
          var images = node.image;
          if (!images) {
            node.image = [absolute];
            changed = true;
            return;
          }
          if (!Array.isArray(images)) images = [images];
          var next = [absolute].concat(
            images.filter(function (url) {
              return String(url) !== String(absolute);
            })
          );
          node.image = next;
          if (variant && variant.sku) {
            node.sku = variant.sku;
            node.mpn = variant.sku;
          }
          changed = true;
        });
        if (changed) {
          if (Array.isArray(data)) script.textContent = JSON.stringify(nodes);
          else if (data['@graph']) {
            data['@graph'] = nodes;
            script.textContent = JSON.stringify(data);
          } else script.textContent = JSON.stringify(nodes[0] || data);
        }
      });
    } catch (eLd) {
      /* ignore malformed JSON-LD */
    }
  };

  /**
   * Tiles gallery only: move the selected swatch’s featured media tile to first
   * position and prioritise it for display + AI/search crawlers.
   */
  RetailProduct.prototype.promoteTilesMedia = function (mediaId, variant) {
    if (!mediaId || !this.isTilesGalleryActive()) return false;

    var grid = this.getTilesGrid();
    if (!grid) return false;

    var slide = grid.querySelector(mediaSelector(mediaId));
    if (!slide) {
      warnDev('Tiles grid has no slide for media', mediaId);
      return false;
    }

    if (grid.firstElementChild !== slide) {
      grid.insertBefore(slide, grid.firstElementChild);
    }

    var titleEl = this.root.querySelector('.product-title, h1');
    var productTitle = titleEl ? String(titleEl.textContent || '').replace(/\s+/g, ' ').trim() : '';
    var optionSummary = this.getSelectedOptionSummary();
    var featuredSrc = null;

    Array.prototype.forEach.call(grid.children, function (el, index) {
      var isFeatured = el === slide;
      el.classList.toggle('is-retail-featured', isFeatured);
      el.setAttribute('data-retail-tile-order', String(index + 1));
      el.setAttribute('aria-current', isFeatured ? 'true' : 'false');

      var img = el.querySelector('img.tcn-slide-image, img');
      if (!img) return;

      if (!img.getAttribute('data-retail-alt-base')) {
        img.setAttribute('data-retail-alt-base', img.getAttribute('alt') || productTitle || '');
      }

      if (isFeatured) {
        img.setAttribute('loading', 'eager');
        img.setAttribute('fetchpriority', 'high');
        img.setAttribute('decoding', 'async');
        var altParts = [];
        if (productTitle) altParts.push(productTitle);
        if (optionSummary) altParts.push(optionSummary);
        if (!altParts.length) altParts.push(img.getAttribute('data-retail-alt-base'));
        img.setAttribute('alt', altParts.join(' — '));
        featuredSrc =
          img.getAttribute('data-tcn-zoom-src') ||
          img.currentSrc ||
          img.getAttribute('src') ||
          null;
      } else {
        img.setAttribute('loading', 'lazy');
        img.removeAttribute('fetchpriority');
      }
    });

    if (!featuredSrc && variant) {
      if (variant.featured_media && variant.featured_media.src) featuredSrc = variant.featured_media.src;
      else if (variant.featured_image && variant.featured_image.src) featuredSrc = variant.featured_image.src;
    }

    if (featuredSrc) this.syncAiSearchImages(featuredSrc, variant);

    log('Tiles: promoted media to first', mediaId);
    return true;
  };

  RetailProduct.prototype.getGalleryMediaMap = function () {
    if (this._galleryMediaMap !== undefined) return this._galleryMediaMap;
    var el = this.root.querySelector('[data-retail-gallery-media]');
    if (!el) {
      this._galleryMediaMap = null;
      return null;
    }
    try {
      this._galleryMediaMap = JSON.parse(el.textContent);
    } catch (e) {
      this._galleryMediaMap = null;
    }
    return this._galleryMediaMap;
  };

  RetailProduct.prototype.getGalleryNodes = function () {
    return this.root.querySelectorAll(
      '.tcn-pdp-slide, .tcn-product-slide, .thumb, .js_prod_sub .thumb, .variant-thumbs .thumb'
    );
  };

  RetailProduct.prototype.allowedMediaForVariant = function (variant) {
    var allowed = Object.create(null);
    var variantId = String(variant.id);
    var map = this.getGalleryMediaMap();
    if (map) {
      var shared = map.shared || [];
      var i;
      for (i = 0; i < shared.length; i++) allowed[String(shared[i])] = true;
      var entry = map.variants && map.variants[variantId];
      var list = entry && entry.media ? entry.media : [];
      for (i = 0; i < list.length; i++) allowed[String(list[i])] = true;
      if (entry && entry.featured) allowed[String(entry.featured)] = true;
      return allowed;
    }
    if (variant.media && variant.media.length) {
      for (var m = 0; m < variant.media.length; m++) allowed[String(variant.media[m])] = true;
    }
    var featured = this.getFeaturedMediaId(variant);
    if (featured) allowed[String(featured)] = true;
    return allowed;
  };

  RetailProduct.prototype.filterGalleryByVariant = function (variant) {
    if (!variant) return false;
    var variantId = String(variant.id);
    var map = this.getGalleryMediaMap();
    var allowed = Object.create(null);
    var ownedAny = false;
    var i;
    var vid;

    if (map) {
      var shared = map.shared || [];
      for (i = 0; i < shared.length; i++) allowed[String(shared[i])] = true;
      var variants = map.variants || {};
      for (vid in variants) {
        if (!Object.prototype.hasOwnProperty.call(variants, vid)) continue;
        if (variants[vid] && variants[vid].media && variants[vid].media.length) ownedAny = true;
      }
      var entry = variants[variantId];
      var list = (entry && entry.media) || [];
      for (i = 0; i < list.length; i++) allowed[String(list[i])] = true;
      if (entry && entry.featured) allowed[String(entry.featured)] = true;
    } else if (variant.media && variant.media.length) {
      ownedAny = true;
      for (i = 0; i < variant.media.length; i++) allowed[String(variant.media[i])] = true;
    }
    var featured = this.getFeaturedMediaId(variant);
    if (featured) allowed[String(featured)] = true;

    var nodes = this.getGalleryNodes();
    var changed = false;
    Array.prototype.forEach.call(nodes, function (el) {
      if (el.classList.contains('slick-cloned')) return;
      var mediaId = mediaIdOf(el);
      if (!mediaId) return;
      var show;
      if (ownedAny) {
        show = !!allowed[mediaId];
      } else {
        var ids = variantIdsOf(el);
        show = !ids || ids.split(',').indexOf(variantId) !== -1;
      }
      var isHidden = el.hasAttribute('hidden');
      if (show && isHidden) {
        el.removeAttribute('hidden');
        changed = true;
      } else if (!show && !isHidden) {
        el.setAttribute('hidden', '');
        changed = true;
      }
    });

    if (window.TcnPdpGallery && typeof window.TcnPdpGallery.run === 'function') {
      window.TcnPdpGallery.run();
    }
    return changed;
  };

  RetailProduct.prototype.goToMedia = function (mediaId, opts) {
    opts = opts || {};
    if (!mediaId) {
      warnDev('Variant has no featured_media; leaving gallery position unchanged');
      return;
    }

    var slideSel = mediaSelector(mediaId);
    var safeId = String(mediaId).replace(/"/g, '');

    /* Desktop tiles: selected swatch image becomes first tile (do not skip mobile carousel sync). */
    var tilesPromoted = this.promoteTilesMedia(mediaId, opts.variant || null);
    if (tilesPromoted && this.stickyImg) {
      var featuredTile =
        this.getTilesGrid() &&
        this.getTilesGrid().querySelector(slideSel);
      var tileImg = featuredTile && featuredTile.querySelector('img');
      if (tileImg) {
        this.stickyImg.src = tileImg.currentSrc || tileImg.src;
      }
    }

    /* Prefer main carousel slides — never thumb nodes (same media id). */
    var targetSlide =
      this.root.querySelector(
        '.tcn-pdp-gallery-mobile-only .js_prod_main.variant-slider > .tcn-pdp-slide' + '[data-media-id="' + safeId + '"], .tcn-pdp-gallery-mobile-only .js_prod_main.variant-slider > .tcn-product-slide' + '[data-media-id="' + safeId + '"]'
      ) ||
      this.root.querySelector('.gallery-control--carousel .js_prod_main .tcn-pdp-slide' + '[data-media-id="' + safeId + '"]:not(.slick-cloned), .gallery-control--carousel .js_prod_main .tcn-product-slide' + '[data-media-id="' + safeId + '"]:not(.slick-cloned)') ||
      this.root.querySelector('.js_prod_main .tcn-pdp-slide[data-media-id="' + safeId + '"]:not(.slick-cloned), .js_prod_main .tcn-product-slide[data-media-id="' + safeId + '"]:not(.slick-cloned)') ||
      this.root.querySelector('.tcn-pdp-tiles-grid ' + slideSel) ||
      this.root.querySelector('.variant-slider .tcn-pdp-slide[data-media-id="' + safeId + '"]:not(.slick-cloned), .variant-slider .tcn-product-slide[data-media-id="' + safeId + '"]:not(.slick-cloned)');

    if (!targetSlide && !tilesPromoted) {
      warnDev('No gallery slide for media id', mediaId);
      return;
    }

    var $ = window.jQuery;
    var $slider = null;
    if ($) {
      var $mobile = $(this.root)
        .find('.tcn-pdp-gallery-mobile-only .js_prod_main.variant-slider, .tcn-pdp-gallery-mobile-only .variant-slider')
        .first();
      var $carousel = $(this.root)
        .find('.gallery-control--carousel .js_prod_main.variant-slider, .gallery-control--carousel .variant-slider')
        .first();
      var $any = $(this.root).find('.js_prod_main.variant-slider').first();
      if ($mobile.length && $mobile.is(':visible')) {
        $slider = $mobile;
      } else if ($carousel.length && $carousel.is(':visible')) {
        $slider = $carousel;
      } else if ($mobile.length && !tilesPromoted) {
        $slider = $mobile;
      } else if ($carousel.length) {
        $slider = $carousel;
      } else if (!tilesPromoted) {
        $slider = $any;
      }
    }

    var movedWithSlick = false;
    if ($slider && $slider.length && $slider.hasClass('slick-initialized')) {
      try {
        var $slides = $slider.find('.slick-slide:not(.slick-cloned)');
        var index = -1;
        $slides.each(function (i) {
          if (mediaIdOf(this) === String(mediaId) || mediaIdOf(this.querySelector('[data-media-id]')) === String(mediaId)) {
            index = i;
            return false;
          }
        });
        if (index >= 0) {
          $slider.slick('slickGoTo', index, Boolean(opts.instant));
          movedWithSlick = true;
        }
      } catch (e) {
        warnDev('slickGoTo failed', e);
      }
    } else if ($slider && $slider.length && !$slider.hasClass('pdp-gallery-native') && !opts._retried) {
      /* Gallery slick may init after retail PDP — retry once, never scroll the page */
      var self = this;
      window.setTimeout(function () {
        self.goToMedia(mediaId, { _retried: true, variant: opts.variant, instant: true });
      }, 120);
    }

    /* Native phone track (≤767): scroll the selected slide into the visible carousel window */
    if (!movedWithSlick) {
      var nativeTrack =
        this.root.querySelector('.tcn-pdp-gallery-mobile-only .variant-slider.pdp-gallery-native.pdp-gallery-track') ||
        this.root.querySelector('.tcn-pdp-gallery-mobile-only .variant-slider.pdp-gallery-track') ||
        this.root.querySelector('.variant-slider.pdp-gallery-native.pdp-gallery-track');
      if (nativeTrack && targetSlide && nativeTrack.contains(targetSlide)) {
        var left =
          targetSlide.getBoundingClientRect().left -
          nativeTrack.getBoundingClientRect().left +
          nativeTrack.scrollLeft;
        try {
          nativeTrack.scrollTo({ left: left, behavior: opts.instant ? 'auto' : 'smooth' });
        } catch (eScroll) {
          nativeTrack.scrollLeft = left;
        }
        /* Ensure lazy slide image loads once it is the current window */
        var slideImg = targetSlide.querySelector('img[data-lazy], img.tcn-slide-image');
        if (slideImg && slideImg.getAttribute('data-lazy')) {
          slideImg.setAttribute('src', slideImg.getAttribute('data-lazy'));
          slideImg.removeAttribute('data-lazy');
        }
      }
    }

    /* Do not call scrollIntoView — it scrolls/grows the whole page on load and variant changes */

    var thumbs = this.root.querySelectorAll(
      '.variant-thumbs [data-media-id]:not(.slick-cloned), .js_prod_sub [data-media-id]:not(.slick-cloned)'
    );
    Array.prototype.forEach.call(thumbs, function (thumb) {
      var active = mediaIdOf(thumb) === String(mediaId);
      thumb.classList.toggle('is-current', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
      if (active && $ && $(thumb).closest('.slick-initialized').length) {
        try {
          var $thumbs = $(thumb).closest('.variant-thumbs, .js_prod_sub');
          var tIndex = $thumbs
            .find('.slick-slide:not(.slick-cloned) [data-media-id], .slick-slide:not(.slick-cloned)[data-media-id]')
            .index($thumbs.find(mediaSelector).first());
          if (tIndex < 0) {
            tIndex = $thumbs.find('[data-media-id]').not('.slick-cloned *').index(thumb);
          }
          if (tIndex >= 0) $thumbs.slick('slickGoTo', tIndex, true);
        } catch (e2) {
          /* ignore */
        }
      }
    });

    if (this.stickyImg && targetSlide) {
      var img = targetSlide.querySelector('img');
      if (img && img.currentSrc) {
        this.stickyImg.src = img.currentSrc;
      } else if (img && img.src) {
        this.stickyImg.src = img.src;
      }
    }

    log('Gallery moved to media', mediaId, movedWithSlick ? 'slick' : tilesPromoted ? 'tiles' : 'native-or-none');
  };

  RetailProduct.prototype.applyVariant = function (variant, opts) {
    opts = opts || {};
    this.updateSelectedStyles();
    this.updateOptionAvailability();

    if (!variant) {
      this.setIdInputs('');
      this.setSubmitEnabled(false, 'Unavailable');
      this.setStatus('This combination is unavailable.', 'error');
      log('No matching variant for options', this.getSelectedOptions());
      return;
    }

    if (Number(variant.product_id) && Number(variant.product_id) !== this.productId) {
      warnDev('Resolved variant does not belong to this product', variant.id);
      this.setIdInputs('');
      this.setSubmitEnabled(false, 'Unavailable');
      this.setStatus('This combination is unavailable.', 'error');
      return;
    }

    this.setIdInputs(variant.id);
    this.updatePrice(variant);
    this.updateSku(variant);
    this.updateShippingCost(variant);

    var available = !!variant.available;
    if (!available) {
      this.setSubmitEnabled(false, 'Sold out');
      this.setStatus('Sold out.', 'soldout');
    } else {
      this.setSubmitEnabled(true, opts.addLabel || 'Add to cart');
      this.setStatus('');
    }

    if (!opts.skipUrl) {
      replaceVariantInUrl(variant.id);
    }

    var mediaId = this.getFeaturedMediaId(variant);
    var picker = this.root.querySelector('[data-retail-variant-picker]');
    var galleryEnabled = !picker || picker.getAttribute('data-retail-update-gallery') !== 'false';
    if (galleryEnabled) {
      this.filterGalleryByVariant(variant);
      if (!opts.skipGallery) {
        this.goToMedia(mediaId, { variant: variant });
      }
    }

    log('Resolved variant', {
      id: variant.id,
      available: available,
      options: variant.options,
      featured_media: mediaId,
      formId: this.idInput && this.idInput.value,
      source: opts.source || 'picker'
    });
  };

  RetailProduct.prototype.resolveFromUi = function (source) {
    var selected = this.getSelectedOptions();
    var incomplete = selected.some(function (v) {
      return v == null || v === '';
    });
    var variant = incomplete ? null : findVariantByOptions(this.variants, selected);

    if (!variant) {
      /* Invalid or incomplete combination — never clear to an empty id (Cart API: "Cannot find variant") */
      variant = firstAvailableOrFirst(this.variants);
      if (variant) this.setOptionValuesFromVariant(variant);
      warnDev('No matching option combination; fell back to', variant && variant.id);
    }

    this.applyVariant(variant, { source: source });
  };

  RetailProduct.prototype.initFromUrlOrDefault = function () {
    var urlId = getUrlVariantId();
    var variant = null;
    var hadInvalidUrl = false;

    if (urlId) {
      variant = findVariantById(this.variants, urlId);
      if (!variant) {
        hadInvalidUrl = true;
        warnDev('Invalid or deleted variant in URL; falling back', urlId);
      }
    }

    if (!variant) {
      var initialId = this.idInput && this.idInput.value;
      variant = findVariantById(this.variants, initialId);
    }

    if (!variant) {
      variant = firstAvailableOrFirst(this.variants);
    }

    if (variant) {
      this.setOptionValuesFromVariant(variant);
      /* skipGallery on load avoids carousel scroll races; still sync tiles + mobile carousel */
      this.applyVariant(variant, { source: 'init', skipGallery: true });
      this.filterGalleryByVariant(variant);
      this.goToMedia(this.getFeaturedMediaId(variant), { variant: variant, instant: true });
      if (hadInvalidUrl) {
        replaceVariantInUrl(variant.id);
      }
    } else {
      this.applyVariant(null, { source: 'init', skipGallery: true });
    }
  };

  RetailProduct.prototype.validateBeforeSubmit = function (source) {
    var rawId = this.idInput && this.idInput.value;
    var variant = findVariantById(this.variants, rawId);

    if (!variant) {
      variant = firstAvailableOrFirst(this.variants);
      if (variant) {
        this.setOptionValuesFromVariant(variant);
        this.applyVariant(variant, { source: 'submit-fallback', skipGallery: true });
        rawId = this.idInput && this.idInput.value;
        variant = findVariantById(this.variants, rawId);
      }
    }

    if (!variant) {
      this.setStatus('Please select an available option.', 'error');
      log('Blocked submit: missing variant id', { source: source });
      return false;
    }

    if (String(variant.id) === String(this.productId)) {
      this.setStatus('Please select an available option.', 'error');
      warnDev('Blocked submit: form id equals product id', variant.id);
      return false;
    }

    if (!variant.available) {
      this.setStatus('Sold out.', 'soldout');
      log('Blocked submit: sold out', variant.id);
      return false;
    }

    this.setIdInputs(variant.id);

    log('Submit allowed', {
      source: source,
      variantId: variant.id,
      formId: this.idInput && this.idInput.value
    });
    return true;
  };

  RetailProduct.prototype.selectOptionFromSwatch = function (swatchEl, source) {
    if (!swatchEl) return;
    var input = swatchEl.querySelector('input[data-retail-option-input]');
    if (!input || input.disabled) return;

    if (!input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      this.resolveFromUi(source || 'swatch-reselect');
    }

    swatchEl.classList.add('is-tooltip-dismissed');
    swatchEl.classList.remove('is-tooltip-open');
    var clearDismiss = function () {
      swatchEl.classList.remove('is-tooltip-dismissed');
      swatchEl.removeEventListener('mouseleave', clearDismiss);
      swatchEl.removeEventListener('blur', clearDismiss, true);
    };
    swatchEl.addEventListener('mouseleave', clearDismiss);
    swatchEl.addEventListener('blur', clearDismiss, true);
  };

  RetailProduct.prototype.bindSwatchTooltips = function () {
    var self = this;
    var swatches = this.root.querySelectorAll('[data-retail-swatch], [data-retail-option-label]');

    Array.prototype.forEach.call(swatches, function (swatch) {
      var tip = swatch.querySelector('[data-retail-swatch-tooltip]');
      if (!tip || tip.getAttribute('data-retail-tooltip-bound') === 'true') return;
      tip.setAttribute('data-retail-tooltip-bound', 'true');

      tip.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.selectOptionFromSwatch(swatch, 'tooltip-click');
      });

      tip.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          self.selectOptionFromSwatch(swatch, 'tooltip-key');
        } else if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          swatch.classList.remove('is-tooltip-open');
          tip.blur();
          var radio = swatch.querySelector('input[data-retail-option-input]');
          if (radio) radio.focus();
        }
      });

      swatch.addEventListener('mouseenter', function () {
        swatch.classList.add('is-tooltip-open');
      });
      swatch.addEventListener('mouseleave', function () {
        swatch.classList.remove('is-tooltip-open');
      });
      tip.addEventListener('mouseenter', function () {
        swatch.classList.add('is-tooltip-open');
      });
      tip.addEventListener('mouseleave', function () {
        if (!swatch.matches(':hover') && !swatch.contains(document.activeElement)) {
          swatch.classList.remove('is-tooltip-open');
        }
      });
    });

    if (!this._tooltipEscapeBound) {
      this._tooltipEscapeBound = true;
      this.root.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        var open = self.root.querySelector(
          '.retail-swatch.is-tooltip-open, .retail-variant-picker__label.is-tooltip-open, .retail-swatch:focus-within, .retail-variant-picker__label:focus-within'
        );
        if (!open) return;
        e.preventDefault();
        open.classList.remove('is-tooltip-open');
        open.classList.add('is-tooltip-dismissed');
        var active = document.activeElement;
        if (active && open.contains(active)) active.blur();
        window.setTimeout(function () {
          open.classList.remove('is-tooltip-dismissed');
        }, 0);
      });
    }
  };

  RetailProduct.prototype.bind = function () {
    if (this.bound) return;
    this.bound = true;
    var self = this;

    Array.prototype.forEach.call(this.optionInputs, function (input) {
      input.addEventListener('change', function () {
        self.resolveFromUi('option-change');
        var dropdown = input.closest('[data-retail-dropdown]');
        if (dropdown) self.toggleDropdown(dropdown, false);
        var swatch = input.closest('[data-retail-swatch], [data-retail-option-label]');
        if (swatch) {
          swatch.classList.add('is-tooltip-dismissed');
          swatch.classList.remove('is-tooltip-open');
        }
      });
    });

    this.bindSwatchTooltips();

    // Keyboard: radios already native; enhance label activation
    Array.prototype.forEach.call(
      this.root.querySelectorAll('[data-retail-option-label]'),
      function (label) {
        label.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            if (e.target && e.target.closest && e.target.closest('[data-retail-swatch-tooltip]')) return;
            e.preventDefault();
            self.selectOptionFromSwatch(label, 'label-key');
          } else if (e.key === 'Escape') {
            label.classList.remove('is-tooltip-open');
            label.classList.add('is-tooltip-dismissed');
            if (document.activeElement && label.contains(document.activeElement)) {
              document.activeElement.blur();
            }
          }
        });
      }
    );

    Array.prototype.forEach.call(
      this.root.querySelectorAll('[data-retail-dropdown]'),
      function (wrap) {
        var trigger = wrap.querySelector('[data-retail-dropdown-trigger]');
        if (!trigger) return;
        trigger.addEventListener('click', function (e) {
          e.preventDefault();
          self.toggleDropdown(wrap);
        });
        trigger.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            self.toggleDropdown(wrap, true);
            var first = wrap.querySelector('[data-retail-dropdown-option]');
            if (first) first.focus();
          } else if (e.key === 'Escape') {
            self.toggleDropdown(wrap, false);
          }
        });
      }
    );

    document.addEventListener('click', function (e) {
      if (!self.root.contains(e.target)) {
        self.closeAllDropdowns();
        return;
      }
      if (!(e.target.closest && e.target.closest('[data-retail-dropdown]'))) {
        self.closeAllDropdowns();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') self.closeAllDropdowns();
    });

    var forms = [];
    if (this.form) forms.push(this.form);
    if (this.stickyForm) forms.push(this.stickyForm);

    forms.forEach(function (form) {
      form.addEventListener(
        'submit',
        function (e) {
          var source = form.hasAttribute('data-retail-sticky-form') ? 'sticky' : 'main';
          if (!self.validateBeforeSubmit(source)) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }
        },
        true
      );
    });

    // Sticky button triggers main form when sticky has no full options
    Array.prototype.forEach.call(
      this.root.querySelectorAll('[data-retail-sticky-trigger]'),
      function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          if (!self.validateBeforeSubmit('sticky')) return;
          if (self.form && typeof self.form.requestSubmit === 'function') {
            self.form.requestSubmit();
          } else if (self.form) {
            self.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        });
      }
    );

    window.addEventListener('popstate', function () {
      self.initFromUrlOrDefault();
    });

    this.bindDetailsDrawer();
  };

  RetailProduct.prototype.bindDetailsDrawer = function () {
    var self = this;
    var root = this.root.querySelector('[data-retail-details-drawer-root]');
    if (!root || root.getAttribute('data-retail-details-bound') === 'true') return;
    root.setAttribute('data-retail-details-bound', 'true');

    var drawer = root.querySelector('[data-retail-details-drawer]');
    var overlay = root.querySelector('[data-retail-details-overlay]');
    var openBtns = this.root.querySelectorAll('[data-retail-details-open]');
    var closeBtns = root.querySelectorAll('[data-retail-details-close]');
    var lastFocus = null;
    var closeTimer = null;

    function getFocusable() {
      if (!drawer) return [];
      return Array.prototype.slice
        .call(
          drawer.querySelectorAll(
            'a[href], button:not([disabled]), textarea, input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        )
        .filter(function (el) {
          return !el.hasAttribute('disabled') && el.offsetParent !== null;
        });
    }

    function setExpanded(open) {
      Array.prototype.forEach.call(openBtns, function (btn) {
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    function openDrawer() {
      if (!root || !drawer) return;
      if (closeTimer) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }
      lastFocus = document.activeElement;
      /* Escape stacking contexts from gallery/columns so the drawer covers the page */
      if (root.parentNode !== document.body) {
        document.body.appendChild(root);
      }
      root.removeAttribute('hidden');
      root.hidden = false;
      /* Force layout so the open transition runs */
      void root.offsetWidth;
      root.classList.add('is-open');
      document.body.classList.add('retail-details-drawer-open');
      setExpanded(true);
      window.requestAnimationFrame(function () {
        var closeBtn = root.querySelector('[data-retail-details-close]');
        if (closeBtn) closeBtn.focus();
        else drawer.focus();
      });
    }

    function closeDrawer() {
      if (!root) return;
      root.classList.remove('is-open');
      document.body.classList.remove('retail-details-drawer-open');
      setExpanded(false);
      closeTimer = window.setTimeout(function () {
        if (!root.classList.contains('is-open')) root.hidden = true;
        closeTimer = null;
      }, 300);
      if (lastFocus && typeof lastFocus.focus === 'function') {
        try {
          lastFocus.focus();
        } catch (e) {
          /* ignore */
        }
      }
    }

    this.openDetailsDrawer = openDrawer;
    this.closeDetailsDrawer = closeDrawer;

    Array.prototype.forEach.call(openBtns, function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        openDrawer();
      });
    });

    Array.prototype.forEach.call(closeBtns, function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        closeDrawer();
      });
    });

    if (overlay) {
      overlay.addEventListener('click', function () {
        closeDrawer();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (!root.classList.contains('is-open')) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeDrawer();
        return;
      }
      if (e.key !== 'Tab' || !drawer) return;
      var focusable = getFocusable();
      if (!focusable.length) {
        e.preventDefault();
        drawer.focus();
        return;
      }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  };

  function addBusinessDays(anchor, count) {
    var date = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
    var added = 0;
    var safety = 0;
    while (added < count && safety < 400) {
      date.setDate(date.getDate() + 1);
      safety += 1;
      var weekday = date.getDay();
      if (weekday !== 0 && weekday !== 6) added += 1;
    }
    return date;
  }

  function formatDeliveryDate(date) {
    try {
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    } catch (e) {
      var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return String(date.getDate()) + ' ' + months[date.getMonth()];
    }
  }

  RetailProduct.prototype.initEstimatedDelivery = function () {
    var el = this.root.querySelector('[data-retail-delivery-window]');
    if (!el) return;
    var minDays = parseInt(el.getAttribute('data-delivery-min'), 10);
    var maxDays = parseInt(el.getAttribute('data-delivery-max'), 10);
    if (!minDays || !maxDays || maxDays < minDays) return;

    var anchorRaw = el.getAttribute('data-delivery-anchor') || '';
    var anchor = anchorRaw ? new Date(anchorRaw + 'T12:00:00') : new Date();
    if (isNaN(anchor.getTime())) anchor = new Date();

    var from = addBusinessDays(anchor, minDays);
    var to = addBusinessDays(anchor, maxDays);
    el.textContent = formatDeliveryDate(from) + ' – ' + formatDeliveryDate(to);

    var note = this.root.querySelector('[data-retail-delivery-range-note]');
    if (note) {
      note.hidden = false;
      note.textContent = minDays + '–' + maxDays + ' business days. ';
    }
  };

  RetailProduct.prototype.init = function () {
    this.bind();
    this.initFromUrlOrDefault();
    this.initEstimatedDelivery();
  };

  function boot() {
    var roots = document.querySelectorAll('[data-retail-product]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__retailProductInit) return;
      root.__retailProductInit = true;
      var instance = new RetailProduct(root);
      instance.init();
      root.__retailProduct = instance;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-retail-product]')) {
      boot();
    }
  });
})();
